import 'element-internals-polyfill'
import { LitElement, html, css } from 'lit'
import routerMixin from './router/router-mixin.js'

import './mobile-nav-bar.js'
import './styles/fonts/Lato-Bold.ttf'
import './styles/fonts/Lato-Regular.ttf'

import { go } from './router/router-mixin.js'
import globalStyles from './styles/global-styles.js'
import { 
  clearSession, 
  getSessionUser, 
  sessionUserIsAdmin,
  isRefreshTokenExpiringSoon,
  getTimeUntilRefreshExpiry,
  isAccessTokenExpired
} from './session/session.js'
import { getTokenRefreshWatcher } from './services/token-refresh-watcher.js'
import { loginIcon, leftArrowIcon } from './styles/icons/action-icons.js'
import Globals from './Globals.js'
import AppEvents from './app-events.js'
import { toast } from './shared/toast.js'
import routes from './router/routes.js'

window.Globals = new Globals()
const ASSERT_BREAKS_THINGS = process.env.ASSERT_BREAKS_THINGS
console.log('ASSERT_BREAKS_THINGS', ASSERT_BREAKS_THINGS)
class AppEnterElement extends routerMixin(LitElement) {
  constructor() {
    super();
    this.tokenWatcher = getTokenRefreshWatcher();
  }

  /** @param {Event} e */
  handleBackClick(e) {
    e.preventDefault()

    if (this.backArrowOverride) return go(this.backArrowOverride.path, this.backArrowOverride.params)
    if (this.backArrowLocation) return go(this.backArrowLocation)

    window.history.back()
  }

  async connectedCallback() {
    super.connectedCallback()
    
    // Set up service worker message listener as the central point
    if ('serviceWorker' in navigator) {
      // Listen for all service worker messages
      navigator.serviceWorker.addEventListener('message', (event) => {
        console.log('1. [Service Worker Message]:', event.data)
        
        // Handle debug messages from service worker
        if (event.data.type === 'SW_DEBUG') {
          console.log('[SW Debug]:', event.data.message)
        }
        
        // Handle queued push events
        if (event.data.type === 'QUEUED_PUSH_EVENTS') {
          console.log('[App] Received queued push events:', event.data.events)
          // Process each queued event
          event.data.events.forEach(pushEvent => {
            window.dispatchEvent(new CustomEvent('service-worker-message', {
              detail: {
                type: 'PUSH_RECEIVED',
                data: pushEvent.data,
                timestamp: pushEvent.timestamp,
                wasQueued: true
              }
            }))
          })
        } else {
          // Dispatch a custom event that the rest of the app can listen to
          window.dispatchEvent(new CustomEvent('service-worker-message', {
            detail: event.data
          }))
        }
      })
      
      // Check if service worker is ready and dispatch event
      navigator.serviceWorker.ready.then((registration) => {
        console.log('2. [Service Worker Ready]:', registration)
        window.dispatchEvent(new CustomEvent('service-worker-ready', {
          detail: { registration }
        }))
        
        // Notify service worker that app has entered
        if (registration.active) {
          registration.active.postMessage({ type: 'APP_ENTER' })
        }
        
        // Check for updates every second
        setInterval(() => {
          registration.update().catch(() => {
            // Silently ignore update failures
          })
        }, 1000)
        
        // Listen for new service worker waiting
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          console.log('[App] New service worker found')
          
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[App] New service worker installed, refreshing...')
              // Automatically refresh the page when new SW is ready
              window.location.reload()
            }
          })
        })
      })
      
      // Handle visibility changes
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && navigator.serviceWorker.controller) {
          console.log('[App] App became visible, checking for queued events')
          navigator.serviceWorker.controller.postMessage({ type: 'APP_ENTER' })
          // checkForDeepLink() (from router-mixin.js) only runs once, in the
          // constructor — that alone only covers a cold launch. A warm resume
          // (app was already open in the background) tapping a notification
          // relies on notificationclick's client.navigate(), which isn't
          // reliable for standalone PWA windows on iOS/WebKit; re-checking
          // here recovers a pending deep link the SW stored even when that
          // navigate silently did nothing.
          this.checkForDeepLink()
        }
      })

      // Handle focus events (for when app comes to foreground on iOS)
      window.addEventListener('focus', () => {
        if (navigator.serviceWorker.controller) {
          console.log('[App] App focused, checking for queued events')
          navigator.serviceWorker.controller.postMessage({ type: 'APP_ENTER' })
          this.checkForDeepLink()
        }
      })
    }
    
    this.addEventListener(
      AppEvents.SET_BACK_ARROW,
      (/** @type {Event & {detail: {path: string, params: Object}}} */ e) => (this.backArrowOverride = e.detail)
    )
    const user = getSessionUser()
    this.loggedIn = !!user
    
    if (this.loggedIn) {
      // Check if refresh token is expiring soon on startup
      if (isRefreshTokenExpiringSoon()) {
        console.log('[App] Refresh token expiring within 24 hours');
        const hours = Math.floor(getTimeUntilRefreshExpiry() / (1000 * 60 * 60));
        if (hours < 1) {
          // Only clear session and redirect if user was actually logged in
          clearSession();
          go('/login?reason=expiring');
          return;
        } else {
          toast(`Your session expires in ${hours} hours. Please log in to extend.`);
        }
      }
      
      // Start proactive token refresh only for authenticated users
      this.tokenWatcher.start();
      
      // PWA-specific: Check tokens when app is opened
      if (window.matchMedia('(display-mode: standalone)').matches) {
        console.log('[App] Running as PWA, checking auth status');
        
        if (isAccessTokenExpired()) {
          this.tokenWatcher.performRefresh();
        }
      }
    }
    
    // Listen for auth expired events - only fires when authenticated user loses auth
    window.addEventListener('auth:expired', () => {
      console.log('[App] Auth expired event received, clearing session and redirecting to login');
      clearSession();
      go('/login');
    });
    
    window.addEventListener(AppEvents.GLOBALS_UPDATED, () => {
      const user = getSessionUser()
      this.loggedIn = !!user
      
      if (this.loggedIn) {
        this.tokenWatcher.start();
      } else {
        this.tokenWatcher.stop();
      }
    })

    // JWT cookies handle session expiration automatically
    this.handleUpdate()
  }

  handleUpdate() {
    // Service worker update handling moved to index.html to avoid conflicts
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Clean up token watcher
    this.tokenWatcher.stop();
  }

  handleLogoClick() {
    if (window.location.pathname.includes(routes.MAP.path)) {
      go(routes.MAP.path)
    }
  }

  render() {
    const homeRoute = sessionUserIsAdmin() ? routes.ADMIN.path : routes.DASHBOARD.path

    // Calculate slot height based on header and nav bar visibility
    let slotHeight = '100%'
    if (this.showHeader && this.showNav) {
      slotHeight = 'calc(100% - var(--app-header-height) - var(--nav-bar-height))'
    } else if (this.showHeader) {
      slotHeight = 'calc(100% - var(--app-header-height))'
    } else if (this.showNav) {
      slotHeight = 'calc(100% - var(--nav-bar-height))'
    }

    return html`
      ${this.showHeader
        ? html`
            <header>
              ${this.showBackArrow
                ? html`
                    <a href="#" @click=${this.handleBackClick}><span class="navIcon">${leftArrowIcon}</span></a>
                    <img
                      @click=${this.handleLogoClick}
                      class="circleResize"
                      src="/images/circleLogo.png"
                      alt="Elkhart Jail Ministry"
                    />
                  `
                : html`
                    <span class="logoResize"
                      ><img @click=${this.handleLogoClick} src="/images/logo.png" alt="Elkhart Jail Ministry"
                    /></span>
                  `}
              ${this.showHeaderQuickNav
                ? html`
                    ${getSessionUser()?.userId
                      ? html`<div></div>`
                      : html`
                          <div class="header-icons">
                            <button class="loginButton sm icon" @click=${() => go(routes.LOGIN.path)}>
                              ${loginIcon} Login
                            </button>
                          </div>
                        `}
                  `
                : html`<div></div>`}
            </header>
          `
        : null}
      <slot ?header=${this.showHeader} style="height: ${slotHeight}"></slot>
      ${this.showNav ? html`<mobile-nav-bar></mobile-nav-bar>` : null}
    `
  }

  static styles = [
    globalStyles,
    css`
      :host {
        padding: 0px;
        background-color: var(--app-white-2);
      }
      header {
        z-index: 1000;
      }
      slot {
        position: fixed;
        overflow-y: scroll;
      }
      slot[header] {
        width: 100%;
        display: block;
        margin-top: var(--app-header-height);
      }
      header {
        display: flex;
        position: fixed;
        top: 0px;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: var(--app-header-height);
        background-color: var(--app-primary-color);
      }
      .header-icons {
        height: calc(var(--app-header-height) * 0.7);
        z-index: 7000;
        display: flex;
        align-items: center;
      }
      .home {
        right: 15px;
      }
      a {
        cursor: pointer;
      }
      a > svg {
        width: 25px;
        height: 25px;
      }
      .logoResize {
        display: flex;
        height: 60px;
        padding: 5px;
      }
      .circleResize {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        height: 60px;
        padding-bottom: 5px;
      }
      .loginButton {
        display: flex;
        background-color: transparent;
        border-color: white;
        margin: 0px;
        /* TODO: figure out why this is needed on ios for header login */
        transform: translate(-10px, 0px);
      }
      .navIcon {
        display: flex;
        color: white;
        padding: 25px;
      }
      @media (max-width: 600px) {
        mobile-nav-bar {
          display: block;
          position: fixed;
          top: -50px;
          left: 50px;
          padding: 20px;
        }
      }
    `,
  ]
}

customElements.define('app-enter', AppEnterElement)
export default AppEnterElement
